import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useT } from "../../shared/i18n";
import { LargeTitle } from "../../shared/ui/LargeTitle";
import {
  createInspectorApi,
  FeedbackQueue,
  JobInspectorPanel,
  type InspectorApi,
} from "./inspector";
import {
  createObservabilityApi,
  LoadQueuesPanel,
  ModelsProvidersPanel,
  type ObservabilityApi,
} from "./observability";
import { AccessPage, createAccessApi, type AccessApi } from "./access";
import { AuditPage, createAuditApi, type AuditApi } from "./audit";
import { createTraceApi, LogsPage, type TraceApi } from "./logs";
import { createUsersApi, UsersPage, type UsersApi } from "./users";
import { AdminDetailHeader } from "./AdminDetailHeader";
import { AdminNav } from "./AdminNav";
import { sectionLabel } from "./adminSections";
import { useShellT } from "./useShellT";
import {
  adminHref,
  DEFAULT_USER_TAB,
  parseAdminLocation,
  splitPath,
  type AdminSectionId,
} from "./adminRoute";
import { readLastSection, storeLastSection } from "./tabStorage";
import { SessionExpired } from "./SessionExpired";
import { SessionExpiredContext } from "./sessionContext";
import { OperationsPanel, type OperationsSnapshot } from "./operations";
import { useWideLayout } from "./useWideLayout";

/** A fresh load re-runs the session check, which offers operator sign-in. */
function reloadApp(): void {
  window.location.assign("/");
}

/** Stand-in for sections whose pages land in later packages: title only. */
function SectionPlaceholder({
  title,
  showTitle,
}: {
  title: string;
  showTitle: boolean;
}) {
  return showTitle ? <LargeTitle title={title} /> : null;
}

export function AdminConsole({
  snapshot,
  inspectorApi,
  observabilityApi,
  usersApi,
  traceApi,
  auditApi,
  accessApi,
  path,
  onNavigate,
  onSignIn,
}: {
  snapshot: OperationsSnapshot;
  /** Replaceable for tests; the default calls the operator API. */
  inspectorApi?: InspectorApi;
  observabilityApi?: ObservabilityApi;
  usersApi?: UsersApi;
  traceApi?: TraceApi;
  auditApi?: AuditApi;
  accessApi?: AccessApi;
  /** Current location, "/admin..." (query string allowed). */
  path: string;
  /** Pushes a history entry (or replaces the current one). */
  onNavigate: (href: string, options?: { replace?: boolean }) => void;
  /** Starts the operator sign-in; defaults to a fresh load of the app. */
  onSignIn?: () => void;
}) {
  const t = useT();
  const shellT = useShellT();
  const api = useMemo(
    () => inspectorApi ?? createInspectorApi(),
    [inspectorApi],
  );
  const observability = useMemo(
    () => observabilityApi ?? createObservabilityApi(),
    [observabilityApi],
  );
  const users = useMemo(() => usersApi ?? createUsersApi(), [usersApi]);
  const traces = useMemo(() => traceApi ?? createTraceApi(), [traceApi]);
  const audit = useMemo(() => auditApi ?? createAuditApi(), [auditApi]);
  const access = useMemo(() => accessApi ?? createAccessApi(), [accessApi]);
  const [expired, setExpired] = useState(false);
  const reportExpired = useCallback(() => {
    setExpired(true);
  }, []);
  const signIn = onSignIn ?? reloadApp;
  const wide = useWideLayout();
  const location = useMemo(() => {
    const { parts, search } = splitPath(path);
    return parseAdminLocation(parts.slice(1), search);
  }, [path]);
  const bareLanding = location.section === undefined;
  const section: AdminSectionId = location.section ?? readLastSection();
  // Narrow screens keep the bare landing as the section list; wide screens
  // always show a section beside the column, so they land on the last one.
  const showList = bareLanding && !wide;
  useEffect(() => {
    if (bareLanding) {
      if (wide && section !== "overview") {
        onNavigate(adminHref({ section }), { replace: true });
      }
      return;
    }
    storeLastSection(section);
  }, [bareLanding, wide, section, onNavigate]);

  const goto = useCallback(
    (href: string) => {
      onNavigate(href);
    },
    [onNavigate],
  );
  // A record open on a narrow screen is a full-screen drill-down with its own
  // back header, so the section header steps aside.
  const drilledDown =
    !wide &&
    ((section === "users" && location.userId !== undefined) ||
      (section === "logs" && location.correlationId !== undefined) ||
      (section === "audit" && location.auditEventId !== undefined) ||
      (section === "access" &&
        (location.operatorId !== undefined || location.roleId !== undefined)));

  let content: ReactNode;
  switch (section) {
    case "overview":
      content = <OperationsPanel snapshot={snapshot} />;
      break;
    case "load":
      content = <LoadQueuesPanel api={observability} />;
      break;
    case "models":
      content = <ModelsProvidersPanel api={observability} />;
      break;
    case "inspector":
      content = <JobInspectorPanel api={api} />;
      break;
    case "feedback":
      content = <FeedbackQueue api={api} />;
      break;
    case "users":
      content = (
        <UsersPage
          api={users}
          traceApi={traces}
          wide={wide}
          userId={location.userId ?? null}
          tab={location.userTab ?? DEFAULT_USER_TAB}
          onNavigate={goto}
        />
      );
      break;
    case "logs":
      content = (
        <LogsPage
          api={traces}
          wide={wide}
          correlationId={location.correlationId ?? null}
          userId={location.logsUserId ?? null}
          onNavigate={goto}
        />
      );
      break;
    case "audit":
      content = (
        <AuditPage
          api={audit}
          wide={wide}
          eventId={location.auditEventId ?? null}
          filter={location.auditFilter ?? {}}
          onNavigate={goto}
        />
      );
      break;
    case "access":
      content = (
        <AccessPage
          api={access}
          wide={wide}
          view={location.accessView ?? "operators"}
          operatorId={location.operatorId ?? null}
          roleId={location.roleId ?? null}
          onNavigate={goto}
          onSignIn={signIn}
        />
      );
      break;
    case "payments":
      content = (
        <SectionPlaceholder
          title={sectionLabel(shellT, section)}
          showTitle={wide}
        />
      );
      break;
  }
  const nav = (
    <AdminNav
      active={showList ? null : section}
      variant={wide ? "column" : "screen"}
      snapshot={snapshot}
      onSelect={(next) => {
        goto(adminHref({ section: next }));
      }}
    />
  );
  return (
    <main
      aria-label={t("admin.title")}
      data-density={wide ? "operator" : "soft"}
      className="mx-auto flex w-full min-w-0 max-w-[1400px] flex-col gap-[18px] overflow-x-hidden px-gutter pb-10 pt-5"
    >
      {expired ? (
        <SessionExpired onSignIn={signIn} />
      ) : (
        <SessionExpiredContext.Provider value={reportExpired}>
          {wide ? (
            <div className="flex min-w-0 items-start gap-6">
              {nav}
              <div className="flex min-w-0 flex-1 flex-col gap-[18px]">
                {content}
              </div>
            </div>
          ) : showList ? (
            <>
              <LargeTitle title={shellT("admin.shell.title")} />
              {nav}
            </>
          ) : (
            <>
              {!drilledDown && (
                <AdminDetailHeader
                  title={sectionLabel(shellT, section)}
                  onBack={() => {
                    goto(adminHref({}));
                  }}
                />
              )}
              <div className="flex min-w-0 flex-col gap-[18px]">{content}</div>
            </>
          )}
        </SessionExpiredContext.Provider>
      )}
    </main>
  );
}
